/* ============================================================================
   Stickr Lab — Upload Engine v1  (assets/sl-upload.js)

   A production-grade artwork upload/validation SERVICE. Detects artwork metadata,
   validates the file, and returns a preview + a structured result. Framework-
   independent: the parsing + validation core is pure JS over bytes (no React,
   Shopify, DOM), so it runs and is fully testable in Node; a thin browser adapter
   (`analyzeFile`) adds the object-URL preview.

   Supported formats: PNG, JPG/JPEG (raster), SVG, PDF (vector).
   Anything else (gif, webp, heic, tiff, ai, eps, …) → unsupported (rejected with a
   clear message).

   Boundaries (this project is the upload service ONLY): no Shopify storage, no
   customer accounts, no proof generation, no pricing, no Builder wiring, no checkout.

   Approved thresholds:
     - Max file size 20 MB (builder UI + Shopify line-item file cap).  [approved]
     - Ideal print DPI 300; below 300 warns (upload-validator.md).  [approved]
     - 150 DPI is the builder's "ready for print" cutoff.           [approved]
   PRODUCT-AGNOSTIC: the engine has NO minimum/maximum dimension or product-specific
   limits. Product rules (min/max size, supported dimensions, Builder limits, Sticker
   Sheet eligibility) live in the product layer and are supplied via the optional
   `opts.rules` validation hooks — see analyzeBytes.

   REJECTION vs PROGRESSION — two different questions, two different owners.

   THIS ENGINE decides only whether a file is USABLE. Only genuinely unusable
   files are rejected: unsupported format, corrupt, over the size cap. Quality
   issues such as DPI and transparency are ADVISORY and never cause upload
   rejection on their own — a soft-printing image is a valid file, and this
   layer will always say so.

   PRODUCT SURFACES may require the customer to acknowledge an advisory before
   continuing. That is a product-flow decision and it lives in the product
   layer; it changes nothing here. The builder now gates cart submission on an
   unresolved low-resolution advisory (see D-099, amended) — the file is still
   accepted by this engine, and the customer is simply asked to choose.

   The distinction matters because it is easy to collapse: "advisory" is about
   whether the FILE is valid, not about whether a SURFACE may wait for an
   answer. This header previously said warnings "never block an order", which
   overstated the engine's authority over a flow it does not own.
   ============================================================================ */
(function (root) {
  'use strict';

  var config = {
    supportedFormats: ['png', 'jpg', 'jpeg', 'svg', 'pdf'],
    /* 20 MB — THE canonical customer-facing upload limit. Nothing else may hard-code a
       number: the validator, the builder UI copy, the Home upload panel, the tests and
       the cart submission all read it from here (see `limits()` below), so they cannot
       drift apart.

       Why 20 and not 50: durable artwork is stored by attaching the original file to the
       Shopify line item, and Shopify caps a line-item file property at 20 MB. At 50 MB a
       customer could pass validation, spend time configuring a sticker, and only then hit
       an opaque failure at Add to Cart. Rejecting at upload with a clear explanation is
       the honest version of the same limit. [approved: Ryan, 2026-07-26] */
    maxFileBytes: 20 * 1024 * 1024,
    /* 30 MEGAPIXELS — the limit the byte cap cannot express.

       20 MB bounds what crosses the network. It says nothing about what an
       image costs once DECODED, and those are different problems. Measured: a
       flat 10000x8000 PNG is 0.32 MB on disk and 80 megapixels in memory. It
       clears the byte check by a factor of sixty, then asks the browser for
       320 MB of RGBA before anything has been drawn. 48 MP measured 192 MB,
       63 MP measured 252 MB. On a phone that is a tab that dies with no message
       attached to it.

       30 MP is where the measurements stop being comfortable — 27 MP decoded in
       842 ms and 108 MB, 48 MP took 1502 ms and 192 MB — and it is far past any
       real print need: the largest sticker this shop sells is 10.5in, which at
       300 DPI is 3150 px, about 10 MP.

       RASTER ONLY. A vector has no pixel count to cap, and applying this to an
       SVG or PDF would reject a good file for a dimension it does not have.
       [approved: Ryan, 2026-09-01] */
    maxRasterPixels: 30 * 1000 * 1000,
    dpiIdeal: 300,                    // [approved: upload-validator.md]
    dpiMinOk: 150                     // [approved: builder hiRes cutoff]
    // NOTE: the engine is PRODUCT-AGNOSTIC — no PRODUCT dimension rules here. No minimum
    // sticker size, no maximum, no supported-dimension list. Those are business decisions
    // and arrive through analyzeBytes(..., { rules: [...] }).
    //
    // The two limits above are a different category: DECODE AND TRANSPORT SAFETY. Neither
    // expresses an opinion about what makes a good sticker — 20 MB is "Shopify will not
    // carry this" and 30 MP is "the browser will not survive decoding this". The engine has
    // always owned the first; the second is its counterpart, and leaving it to the product
    // layer would mean every surface re-deriving the same memory arithmetic.
  };

  /* Megabytes for humans: one decimal, but whole numbers stay whole ("20 MB", not
     "20.0 MB"). Used by both the error message and `limits()` so the number a customer
     sees before uploading is produced by the same code as the number in the rejection. */
  function mb(bytes) {
    var v = bytes / 1048576;
    return (Math.round(v * 10) / 10).toString();
  }
  /* Megapixels for humans, same rule as `mb`: one decimal, whole numbers stay
     whole ("30 MP", not "30.0 MP"). */
  function mp(pixels) {
    var v = pixels / 1000000;
    return (Math.round(v * 10) / 10).toString();
  }

  /* THE single source of truth for every customer-facing limit string.
     UI code must call this rather than hard-coding "up to 20 MB" — that is exactly how
     the copy and the validator drift apart, and the customer discovers the difference by
     being rejected after doing the work. */
  function limits() {
    var formats = config.supportedFormats
      .filter(function (f) { return f !== 'jpeg'; })   // jpg and jpeg are one format to a customer
      .map(function (f) { return f.toUpperCase(); });
    return {
      maxFileBytes: config.maxFileBytes,
      maxFileMb: mb(config.maxFileBytes),
      maxFileLabel: 'up to ' + mb(config.maxFileBytes) + ' MB',
      maxRasterPixels: config.maxRasterPixels,
      maxRasterMp: mp(config.maxRasterPixels),
      supportedFormats: config.supportedFormats.slice(),
      formatsLabel: formats.join(' · '),
      /* THE PICKER'S `accept`, from the same list the validator enforces.
         It was `image/*,.pdf,.svg`, which offered the customer WEBP, HEIC and
         GIF and then rejected them after they had chosen — a mismatch with no
         upside. Extensions, not a MIME wildcard, so the two cannot drift. */
      /* the picker also offers the phone formats prepareFile converts (HEIC/HEIF/WEBP/AVIF, WG2 24 Oct
         2026): without them iOS greys an iPhone photo out in Files and the conversion is never reached */
      acceptAttr: config.supportedFormats.concat(['heic', 'heif', 'webp', 'avif']).map(function (f) { return '.' + f; }).join(',') + ',image/heic,image/heif,image/webp,image/avif',
      // The whole hint, ready to render: "PNG · JPG · SVG · PDF · up to 20 MB"
      uploadHint: formats.join(' · ') + ' · up to ' + mb(config.maxFileBytes) + ' MB',
      /* The full rule, for helper text. The megapixel limit is deliberately NOT
         in `uploadHint`: it applies to rasters only, and a two-clause sentence
         in a drop zone is clutter. */
      limitsDetail: 'PNG or JPG: up to ' + mb(config.maxFileBytes) + ' MB / ' +
        mp(config.maxRasterPixels) + ' MP · SVG or PDF: up to ' + mb(config.maxFileBytes) + ' MB',
      dpiIdeal: config.dpiIdeal,
      dpiMinOk: config.dpiMinOk
    };
  }

  // ---- byte helpers -------------------------------------------------------
  function u16BE(b, o) { return (b[o] << 8) | b[o + 1]; }
  function u32BE(b, o) { return ((b[o] << 24) | (b[o + 1] << 16) | (b[o + 2] << 8) | b[o + 3]) >>> 0; }
  function decode(bytes, enc) {
    try { return new TextDecoder(enc || 'utf-8').decode(bytes); }
    catch (e) { var s = ''; for (var i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]); return s; }
  }
  function round(n) { return Math.round(n); }

  // ---- format detection (magic bytes first, extension as fallback) --------
  function extOf(name) { var m = /\.([a-z0-9]+)$/i.exec(name || ''); return m ? m[1].toLowerCase() : ''; }
  function detectFormat(b, name) {
    var ext = extOf(name);
    if (b && b.length >= 8 && b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4E && b[3] === 0x47)
      return { format: 'png', kind: 'raster', isVector: false };
    if (b && b.length >= 3 && b[0] === 0xFF && b[1] === 0xD8 && b[2] === 0xFF)
      return { format: 'jpg', kind: 'raster', isVector: false };
    if (b && b.length >= 5 && b[0] === 0x25 && b[1] === 0x50 && b[2] === 0x44 && b[3] === 0x46) // %PDF
      return { format: 'pdf', kind: 'vector', isVector: true };
    if (b && b.length) {
      var head = decode(b.subarray(0, Math.min(b.length, 1024)), 'utf-8');
      if (/<svg[\s>]/i.test(head)) return { format: 'svg', kind: 'vector', isVector: true };
      if (b[0] === 0x47 && b[1] === 0x49 && b[2] === 0x46) return { format: 'gif', kind: 'raster', isVector: false };
      if (b.length >= 12 && b[0] === 0x52 && b[1] === 0x49 && b[2] === 0x46 && b[8] === 0x57 && b[9] === 0x45)
        return { format: 'webp', kind: 'raster', isVector: false };
      if (b.length >= 12 && b[4] === 0x66 && b[5] === 0x74 && b[6] === 0x79 && b[7] === 0x70) {
        var brand = decode(b.subarray(8, 12), 'utf-8'); if (/hei|hev|mif/i.test(brand)) return { format: 'heic', kind: 'raster', isVector: false };
      }
      if ((b[0] === 0x49 && b[1] === 0x49 && b[2] === 0x2A) || (b[0] === 0x4D && b[1] === 0x4D && b[3] === 0x2A))
        return { format: 'tiff', kind: 'raster', isVector: false };
    }
    // fall back to extension (jpeg alias -> jpg)
    if (ext === 'jpeg') ext = 'jpg';
    if (ext === 'png') return { format: 'png', kind: 'raster', isVector: false };
    if (ext === 'jpg') return { format: 'jpg', kind: 'raster', isVector: false };
    if (ext === 'svg') return { format: 'svg', kind: 'vector', isVector: true };
    if (ext === 'pdf') return { format: 'pdf', kind: 'vector', isVector: true };
    return { format: ext || 'unknown', kind: null, isVector: false };
  }

  // ---- parsers (return {widthPx,heightPx,dpi,hasAlpha} or null on corrupt) --
  function parsePNG(b) {
    if (b.length < 33) return null;
    // IHDR is the first chunk: length(8..11)=13, type(12..15)='IHDR', data at 16
    if (decode(b.subarray(12, 16), 'utf-8') !== 'IHDR') return null;
    var widthPx = u32BE(b, 16), heightPx = u32BE(b, 20), colorType = b[25];
    if (!widthPx || !heightPx) return null;
    var dpi = null, hasTRNS = false;
    var off = 8;
    while (off + 8 <= b.length) {
      var len = u32BE(b, off), type = decode(b.subarray(off + 4, off + 8), 'utf-8'), d = off + 8;
      if (type === 'pHYs' && d + 9 <= b.length) {
        var ppuX = u32BE(b, d), unit = b[d + 8];
        if (unit === 1) dpi = round(ppuX * 0.0254);           // pixels/metre -> DPI
      }
      if (type === 'tRNS') hasTRNS = true;
      if (type === 'IEND') break;
      off = d + len + 4;                                       // +CRC
      if (len < 0 || off <= d) break;
    }
    var hasAlpha = (colorType === 6 || colorType === 4) ? true : (colorType === 3 ? hasTRNS : false);
    return { widthPx: widthPx, heightPx: heightPx, dpi: dpi, hasAlpha: hasAlpha };
  }

  function parseJPEG(b) {
    if (b.length < 4 || b[0] !== 0xFF || b[1] !== 0xD8) return null;
    var off = 2, dpi = null, widthPx = null, heightPx = null;
    while (off + 4 <= b.length) {
      if (b[off] !== 0xFF) { off++; continue; }
      var marker = b[off + 1];
      if (marker === 0xD9 || marker === 0xDA) break;          // EOI / SOS
      if (marker >= 0xD0 && marker <= 0xD7) { off += 2; continue; } // RST (no length)
      var len = u16BE(b, off + 2), seg = off + 4;
      if (marker === 0xE0 && decode(b.subarray(seg, seg + 4), "utf-8") === "JFIF") {
        var units = b[seg + 7], xd = u16BE(b, seg + 8);
        if (units === 1) dpi = xd; else if (units === 2) dpi = round(xd * 2.54);
      }
      // SOF markers carry the image size (skip DHT C4, JPG C8, DAC CC)
      if (marker >= 0xC0 && marker <= 0xCF && marker !== 0xC4 && marker !== 0xC8 && marker !== 0xCC && widthPx == null) {
        heightPx = u16BE(b, seg + 1); widthPx = u16BE(b, seg + 3);
      }
      off = seg + len - 2;
      if (len < 2) break;
    }
    if (!widthPx || !heightPx) return null;
    return { widthPx: widthPx, heightPx: heightPx, dpi: dpi, hasAlpha: false }; // JPEG never has alpha
  }

  function lenToInches(value, unit) {
    switch ((unit || '').toLowerCase()) {
      case 'in': return value;
      case 'pt': return value / 72;
      case 'pc': return value / 6;
      case 'cm': return value / 2.54;
      case 'mm': return value / 25.4;
      case 'px': case '': return value / 96;   // CSS px
      default: return null;                     // %, em, etc. -> unknown
    }
  }
  function parseSVG(b) {
    var s = decode(b, 'utf-8');
    var tag = /<svg\b[^>]*>/i.exec(s);
    if (!tag) return null;
    var t = tag[0];
    function attr(name) { var m = new RegExp(name + '\\s*=\\s*["\']([^"\']+)["\']', 'i').exec(t); return m ? m[1].trim() : null; }
    function num(str) { var m = /^([\d.\-+eE]+)\s*([a-z%]*)$/.exec(str || ''); return m ? { v: parseFloat(m[1]), u: m[2] } : null; }
    var wIn = null, hIn = null, wRaw = num(attr('width')), hRaw = num(attr('height'));
    if (wRaw) wIn = lenToInches(wRaw.v, wRaw.u);
    if (hRaw) hIn = lenToInches(hRaw.v, hRaw.u);
    var vb = attr('viewBox');
    if ((wIn == null || hIn == null) && vb) {
      var p = vb.split(/[\s,]+/).map(parseFloat);
      if (p.length === 4 && p[2] > 0 && p[3] > 0) { if (wIn == null) wIn = p[2] / 96; if (hIn == null) hIn = p[3] / 96; }
    }
    if (wIn == null || hIn == null || !(wIn > 0) || !(hIn > 0)) return { widthIn: null, heightIn: null, dpi: null, hasAlpha: true, vector: true };
    return { widthIn: round2(wIn), heightIn: round2(hIn), dpi: null, hasAlpha: true, vector: true };
  }

  function parsePDF(b) {
    var s = decode(b, 'latin1');
    if (s.indexOf('%PDF-') !== 0 && s.indexOf('%PDF-') < 0) return null;
    var m = /\/MediaBox\s*\[\s*([\d.\-]+)\s+([\d.\-]+)\s+([\d.\-]+)\s+([\d.\-]+)\s*\]/.exec(s);
    if (!m) return { widthIn: null, heightIn: null, dpi: null, hasAlpha: null, vector: true }; // valid-ish PDF, size unknown
    var w = (parseFloat(m[3]) - parseFloat(m[1])) / 72, h = (parseFloat(m[4]) - parseFloat(m[2])) / 72;
    if (!(w > 0) || !(h > 0)) return { widthIn: null, heightIn: null, dpi: null, hasAlpha: null, vector: true };
    return { widthIn: round2(w), heightIn: round2(h), dpi: null, hasAlpha: null, vector: true };
  }

  function round2(n) { return Math.round((n + Number.EPSILON) * 100) / 100; }
  function orientationOf(w, h) { if (w == null || h == null) return null; return w > h ? 'landscape' : (h > w ? 'portrait' : 'square'); }

  /* ---- core: analyze bytes → { metadata, status, accepted, warnings } ----
     opts: { fileSize?: number (defaults to bytes.length), targetWidthIn?: number } */
  function analyzeBytes(bytes, fileName, opts) {
    opts = opts || {};
    var b = bytes && bytes.length != null ? bytes : new Uint8Array(0);
    var fileSize = opts.fileSize != null ? opts.fileSize : b.length;
    var warnings = [];
    function warn(code, level, message) { warnings.push({ code: code, level: level, message: message }); }

    var det = detectFormat(b, fileName);
    var format = det.format === 'jpeg' ? 'jpg' : det.format;

    var meta = {
      fileName: fileName || null, extension: extOf(fileName), format: format,
      fileSize: fileSize, kind: det.kind, isVector: !!det.isVector,
      widthPx: null, heightPx: null, widthIn: null, heightIn: null,
      embeddedDpi: null, effectivePrintDpi: null, hasAlpha: null,
      orientation: null, aspectRatio: null
    };

    /* hard: file too large.
       The message states the actual size, the limit, and what to do about it. "File is
       over the limit" tells a customer they failed without telling them how to succeed,
       and this is the last moment before they would otherwise hit an opaque Add-to-Cart
       failure. */
    if (fileSize > config.maxFileBytes)
      warn('file-too-large', 'error',
        'This file is ' + mb(fileSize) + ' MB. The maximum is ' + mb(config.maxFileBytes) +
        ' MB — try exporting it at a smaller size, or as a JPG instead of a PNG.');

    // hard: unsupported format
    if (config.supportedFormats.indexOf(format) === -1) {
      /* a phone photo the browser could not convert (see prepareFile) says what it is and what to do */
      if (format === 'heic' || format === 'heif')
        warn('unsupported-format', 'error', 'This is an iPhone photo (HEIC) that this browser can’t open. Save it as a JPG or PNG (in Photos: Share, then Save as JPEG, or take a screenshot) and upload that.');
      else if (format === 'webp' || format === 'avif')
        warn('unsupported-format', 'error', 'This ' + format.toUpperCase() + ' image couldn’t be opened here. Save it as a PNG or JPG and upload that.');
      else
      warn('unsupported-format', 'error', 'Unsupported file type. Please upload PNG, JPG, SVG, or PDF.');
      return finalize(meta, warnings);
    }

    // parse per format
    var parsed = null;
    if (format === 'png') parsed = parsePNG(b);
    else if (format === 'jpg') parsed = parseJPEG(b);
    else if (format === 'svg') parsed = parseSVG(b);
    else if (format === 'pdf') parsed = parsePDF(b);

    if (!parsed) { warn('corrupt-file', 'error', 'This file appears to be corrupt or unreadable.'); return finalize(meta, warnings); }

    // fold parsed metadata
    if (det.kind === 'raster') {
      meta.widthPx = parsed.widthPx; meta.heightPx = parsed.heightPx;
      meta.embeddedDpi = parsed.dpi; meta.hasAlpha = parsed.hasAlpha;
      meta.orientation = orientationOf(parsed.widthPx, parsed.heightPx);
      meta.aspectRatio = parsed.widthPx && parsed.heightPx ? round2(parsed.widthPx / parsed.heightPx) : null;
      if (parsed.dpi && parsed.widthPx) { meta.widthIn = round2(parsed.widthPx / parsed.dpi); meta.heightIn = round2(parsed.heightPx / parsed.dpi); }

      /* hard: too many pixels to decode safely. RASTER ONLY — this branch is
         `det.kind === 'raster'`, so no vector reaches it.

         This is the VERDICT. It is not the guard: by the time a caller has an
         analysis in hand it may already have decoded the image, which is the
         expensive part. `probeRaster` below exists so the builder can ask the
         cheap question first and never construct an Image at all. */
      var px = (parsed.widthPx || 0) * (parsed.heightPx || 0);
      if (px > config.maxRasterPixels) {
        warn('image-too-many-pixels', 'error',
          'This image is ' + parsed.widthPx + ' × ' + parsed.heightPx + ' pixels (' +
          mp(px) + ' megapixels). The maximum is ' + mp(config.maxRasterPixels) +
          ' MP — try exporting it at a smaller pixel size. Print quality needs far less: ' +
          'a 10 inch sticker only needs about 3000 pixels across.');
      }
    } else { // vector
      meta.widthIn = parsed.widthIn; meta.heightIn = parsed.heightIn;
      meta.hasAlpha = parsed.hasAlpha;
      meta.orientation = orientationOf(parsed.widthIn, parsed.heightIn);
      meta.aspectRatio = parsed.widthIn && parsed.heightIn ? round2(parsed.widthIn / parsed.heightIn) : null;
    }

    // ---- quality warnings: ADVISORY, never a rejection — PRODUCT-AGNOSTIC only ----
    // These never make a file invalid. A product surface may still choose to ask
    // the customer to acknowledge one before continuing; that is its call, not this
    // engine's, and nothing here changes when it does.
    // DPI (raster). Needs a target print width to judge; otherwise report only. DPI never
    // causes rejection on its own.
    if (det.kind === 'raster') {
      if (parsed.dpi == null) warn('no-embedded-dpi', 'info', 'No embedded resolution found; print quality depends on the final print size.');
      if (opts.targetWidthIn > 0 && parsed.widthPx) {
        // effective print DPI is the worst (lowest) axis; height target is optional
        var dpiW = parsed.widthPx / opts.targetWidthIn;
        var dpiH = (opts.targetHeightIn > 0 && parsed.heightPx) ? parsed.heightPx / opts.targetHeightIn : dpiW;
        var eff = round(Math.min(dpiW, dpiH));
        meta.effectivePrintDpi = eff;
        if (eff < config.dpiMinOk) warn('very-low-dpi', 'warn', 'Very low resolution (' + eff + ' DPI) for a ' + opts.targetWidthIn + '" print — it may look soft. A higher-resolution file is recommended.');
        else if (eff < config.dpiIdeal) warn('low-dpi', 'warn', 'Below the ideal 300 DPI (' + eff + ' DPI) for a ' + opts.targetWidthIn + '" print — still printable, but a sharper file is better.');
      }
      // transparency is METADATA (meta.hasAlpha); a neutral, non-blocking note only.
      if (parsed.hasAlpha === false) warn('no-transparency', 'info', 'No transparent background (alpha channel) detected.');
    }
    // NOTE: no minimum/maximum dimension checks — those are product rules (see hooks below).

    // ---- product-supplied validation hooks (opts.rules) ----
    // The engine stays product-agnostic; products (Builder, Sticker Sheet, …) pass their own
    // size / dimension / eligibility rules. Each hook receives the metadata and may return a
    // warning `{ code, level, message }` (or an array, or null). A returned 'error' level rejects.
    if (Array.isArray(opts.rules)) {
      opts.rules.forEach(function (rule) {
        var out;
        try { out = rule(meta); } catch (e) { out = { code: 'rule-error', level: 'warn', message: 'A product rule failed to run.' }; }
        if (!out) return;
        (Array.isArray(out) ? out : [out]).forEach(function (w) {
          if (w && w.code) warnings.push({ code: w.code, level: w.level || 'warn', message: w.message || '' });
        });
      });
    }

    return finalize(meta, warnings);
  }

  /* ---- the ONE quality verdict (D-099) ------------------------------------
     Three states, derived from the thresholds this file already owns and the
     effective print DPI it already computes. It adds no arithmetic: a second
     DPI calculator is exactly what must not exist.

       good     >= dpiIdeal (300), or not measurable, or VECTOR
       caution  >= dpiMinOk (150) and < 300 — softer than ideal, still fine
       low      <  dpiMinOk (150) — visible softness reasonably likely

     VECTOR IS ALWAYS GOOD, and that is not a special case bolted on here: the
     DPI block above runs only for `kind === 'raster'`, so a PDF or SVG never
     has an `effectivePrintDpi` to judge. A vector original must never be called
     low-resolution because something downstream rasterised it for a preview —
     the source is what is being judged, not a picture of the source.

     `null` in, `good` out: absence of evidence is not evidence of a problem,
     and warning a customer on a measurement we do not have is worse than
     staying quiet.

     @param {Object|null} result  an analyzeBytes() result
     @returns {{level:string, dpi:(number|null), vector:boolean, code:(string|null)}}
  */
  function qualityOf(result) {
    var meta = result && result.metadata;
    /* NOT MEASURED YET is not GOOD (D-101).
       A raster whose bytes have not arrived is unknown; a vector has nothing to
       measure and IS fine. Collapsing the two let a file that simply had not
       finished loading be declared good permanently — nothing warned, and
       nothing ever re-checked. Nothing is warned on `unknown` either, but the
       caller can tell the difference and wait for the real answer. */
    if (!meta) return { level: 'unknown', dpi: null, vector: false, code: null };
    var vector = (meta.kind && meta.kind !== 'raster') || meta.vector === true;
    if (vector) return { level: 'good', dpi: null, vector: true, code: null };
    var eff = meta.effectivePrintDpi;
    if (!(eff > 0)) return { level: 'unknown', dpi: null, vector: false, code: null };
    if (eff < config.dpiMinOk) return { level: 'low', dpi: eff, vector: false, code: 'very-low-dpi' };
    if (eff < config.dpiIdeal) return { level: 'caution', dpi: eff, vector: false, code: 'low-dpi' };
    return { level: 'good', dpi: eff, vector: false, code: null };
  }

  /* ---- the SOURCE-FILE advisory (D-103) -----------------------------------
     A DIFFERENT QUESTION FROM DPI, and deliberately kept apart.

       qualityOf()        does this raster have enough pixels for the size the
                          customer ordered?  (300 ideal / 150 floor)
       sourceAdvisoryFor() is this the KIND of file a logo prints cleanest from?

     A 4032x3024 phone photo on a 3in sticker is ~1008 DPI — excellent by the
     first question, and still worth a quiet word about the second, because the
     customer may have the original vector sitting on their desktop. Both can be
     true at once; neither suppresses the other.

     CLASSIFIED BY EXTENSION FIRST, MIME second. The extension is what the
     validator, the stored filename and `mimeOf()` all key on, so it is the
     canonical fact here; MIME is a fallback for a file that arrives without a
     usable name.

     PNG IS DELIBERATELY NOT ADVISED. A transparent, high-resolution PNG is
     exactly what a sticker customer should be uploading, and nagging that
     customer would train everyone to dismiss the dialog.

     HEIC/HEIF/WEBP are classified for completeness but are currently
     UNREACHABLE: `supportedFormats` is png/jpg/jpeg/svg/pdf, so the validator
     rejects them before this runs. iOS normally transcodes a photo-library
     HEIC to JPEG on its way into a file input, which is why phone photos arrive
     as .jpg. If any of the three is ever accepted, this is already the right
     answer for it — WEBP sits with JPEG because it is likewise a lossy
     photo-first container, not because it is "worse". */
  var SOURCE_ADVISE = ['jpg', 'jpeg', 'heic', 'heif', 'webp'];
  var SOURCE_PREFERRED = ['svg', 'pdf'];
  var SOURCE_ACCEPTABLE = ['png'];

  var SOURCE_MIME = {
    'image/jpeg': 'jpg', 'image/jpg': 'jpg',
    'image/heic': 'heic', 'image/heif': 'heif',
    'image/webp': 'webp',
    'image/png': 'png', 'image/svg+xml': 'svg', 'application/pdf': 'pdf'
  };

  /**
   * Should this SOURCE FILE get the "want the cleanest print?" advisory?
   *
   * @param {String} fileName   the customer's filename (canonical)
   * @param {String} [mimeType] fallback when the name carries no usable extension
   * @returns {{advise:boolean, ext:string, category:string}}
   *          category: 'photo' | 'acceptable' | 'preferred' | 'unknown'
   */
  function sourceAdvisoryFor(fileName, mimeType) {
    var ext = extOf(fileName);
    if (!ext || (SOURCE_ADVISE.indexOf(ext) === -1 &&
                 SOURCE_PREFERRED.indexOf(ext) === -1 &&
                 SOURCE_ACCEPTABLE.indexOf(ext) === -1)) {
      var fromMime = SOURCE_MIME[String(mimeType || '').toLowerCase().split(';')[0].trim()];
      if (fromMime) ext = fromMime;
    }
    if (SOURCE_ADVISE.indexOf(ext) !== -1) return { advise: true, ext: ext, category: 'photo' };
    if (SOURCE_PREFERRED.indexOf(ext) !== -1) return { advise: false, ext: ext, category: 'preferred' };
    if (SOURCE_ACCEPTABLE.indexOf(ext) !== -1) return { advise: false, ext: ext, category: 'acceptable' };
    /* Never advise on a file we cannot name. An unknown extension is either
       rejected upstream or something we have no opinion about, and guessing
       would produce exactly the false positive this policy avoids. */
    return { advise: false, ext: ext || '', category: 'unknown' };
  }

  /** A coarse bucket for telemetry. Never the exact DPI, never the filename. */
  function qualityBucket(dpi) {
    if (!(dpi > 0)) return 'unknown';
    if (dpi < 75) return 'under-75';
    if (dpi < config.dpiMinOk) return '75-149';
    if (dpi < 200) return '150-199';
    if (dpi < config.dpiIdeal) return '200-299';
    return '300-plus';
  }

  function finalize(meta, warnings) {
    var hasError = warnings.some(function (w) { return w.level === 'error'; });
    return {
      status: hasError ? 'rejected' : 'accepted',
      accepted: !hasError,
      metadata: meta,
      warnings: warnings
      // NOTE: no price, no storage, no proof — upload service only.
    };
  }

  /* ---- browser adapter: analyze a File + attach an object-URL preview ----
     Returns a Promise. Preview + fileSize come from the File; the metadata and
     validation come from analyzeBytes over the file's bytes. */
  function analyzeFile(file, opts) {
    opts = opts || {};
    if (typeof file === 'undefined' || file === null) return Promise.reject(new Error('no file'));
    var read = file.arrayBuffer ? file.arrayBuffer()
      : new Promise(function (res, rej) { var r = new FileReader(); r.onload = function () { res(r.result); }; r.onerror = function () { rej(r.error); }; r.readAsArrayBuffer(file); });
    return read.then(function (buf) {
      var bytes = new Uint8Array(buf);
      var result = analyzeBytes(bytes, file.name, { fileSize: file.size, targetWidthIn: opts.targetWidthIn });
      result.preview = null;
      try { if (result.accepted && typeof URL !== 'undefined' && URL.createObjectURL) result.preview = URL.createObjectURL(file); } catch (e) {}
      return result;
    });
  }

  /* ---- PHONE PHOTOS: convert in the browser before validation (WG2, 24 Oct 2026) ----------
     An iPhone keeps photos as HEIC/HEIF and a photo saved from a web page on Android is often
     WEBP (or AVIF); none of them is a format the production pipeline takes. Where the browser
     itself can decode the file (Safari decodes HEIC; every current browser decodes WEBP), it is
     drawn at its full pixel size and re-saved as a PNG: same pixels, same transparency, no
     resampling unless the picture exceeds MAX_CONVERT_PIXELS (a phone's canvas limit). The
     result is an ordinary PNG File, so validation, the DPI check, the preview and the order's
     original are exactly what they are for any PNG. Where the browser cannot decode it, the
     file goes on unchanged and the validator's refusal names the format and the fix.
     prepareFile(file) -> Promise<{ file, converted, from }>. Never rejects. */
  var CONVERTIBLE = ['heic', 'heif', 'webp', 'avif'];
  var MAX_CONVERT_PIXELS = 16.7e6;
  function headFormat(file) {
    return new Promise(function (resolve) {
      try {
        var slice = file.slice(0, 64);
        var done = function (buf) { try { resolve(detectFormat(new Uint8Array(buf), file.name).format); } catch (e) { resolve(null); } };
        if (slice.arrayBuffer) slice.arrayBuffer().then(done, function () { resolve(null); });
        else { var r = new FileReader(); r.onload = function () { done(r.result); }; r.onerror = function () { resolve(null); }; r.readAsArrayBuffer(slice); }
      } catch (e) { resolve(null); }
    });
  }
  /* A conversion that never answers must not hold the upload forever (P0, 27 Oct 2026: Ryan's iPhone photo; the
     same limit -49 set for the preview, ICJ-WEBP 9dc4ab2). Past it the original goes on unconverted and the
     validator says honestly that this HEIC/WEBP couldn't be opened here. */
  var CONVERT_LIMIT_MS = 20000;
  function decodeToPng(file) {
    return new Promise(function (resolve, reject) {
      /* ONE outcome only, and the full-size canvas (up to 16.7 MP, ~67 MB) released the moment it has answered or
         failed: iOS counts canvas memory against one fixed total, and a canvas left for the garbage collector can
         make the NEXT getContext/toBlob fail or never answer. A late answer after the limit only cleans up. */
      var settled = false, canvas = null, cleanup = null;
      var releaseAll = function () {
        if (canvas) { try { canvas.width = 0; canvas.height = 0; } catch (e) {} canvas = null; }
        if (cleanup) { try { cleanup(); } catch (e) {} cleanup = null; }
      };
      var finish = function (err, value) {
        if (settled) { releaseAll(); return; }
        settled = true; clearTimeout(limit); releaseAll();
        if (err) reject(err); else resolve(value);
      };
      var limit = setTimeout(function () { finish(new Error('convert_timeout')); }, CONVERT_LIMIT_MS);
      var draw = function (src, w, h, done) {
        cleanup = done;
        if (settled) { releaseAll(); return; }
        try {
          var k = (w * h > MAX_CONVERT_PIXELS) ? Math.sqrt(MAX_CONVERT_PIXELS / (w * h)) : 1;
          var cw = Math.max(1, Math.round(w * k)), ch = Math.max(1, Math.round(h * k));
          var c = canvas = document.createElement('canvas'); c.width = cw; c.height = ch;
          var g = c.getContext('2d');
          if (!g) { finish(new Error('no_canvas_context')); return; }
          g.drawImage(src, 0, 0, cw, ch);
          if (cleanup) { try { cleanup(); } catch (e) {} cleanup = null; }   // the decoded source is no longer needed
          c.toBlob(function (blob) { blob ? finish(null, { blob: blob, scaled: k < 1 }) : finish(new Error('toBlob')); }, 'image/png');
        } catch (e) { finish(e); }
      };
      var viaImage = function () {
        if (settled) return;
        try {
          var url = URL.createObjectURL(file), img = new Image();
          img.onload = function () { draw(img, img.naturalWidth, img.naturalHeight, function () { URL.revokeObjectURL(url); }); };
          img.onerror = function () { URL.revokeObjectURL(url); finish(new Error('decode')); };
          img.src = url;
        } catch (e) { finish(e); }
      };
      if (typeof createImageBitmap === 'function')
        createImageBitmap(file).then(function (bmp) { draw(bmp, bmp.width, bmp.height, function () { try { bmp.close(); } catch (e) {} }); }, viaImage);
      else viaImage();
    });
  }
  function prepareFile(file) {
    if (!file || typeof document === 'undefined') return Promise.resolve({ file: file, converted: false, from: null });
    return headFormat(file).then(function (fmt) {
      if (CONVERTIBLE.indexOf(fmt) === -1) return { file: file, converted: false, from: fmt };
      return decodeToPng(file).then(function (r) {
        var base = String(file.name || 'photo').replace(/.[a-z0-9]+$/i, '') || 'photo';
        var out;
        try { out = new File([r.blob], base + '.png', { type: 'image/png', lastModified: file.lastModified || Date.now() }); }
        catch (e) { out = r.blob; try { out.name = base + '.png'; } catch (x) {} }
        return { file: out, converted: true, from: fmt, scaled: r.scaled };
      }, function () { return { file: file, converted: false, from: fmt, failed: true }; });
    }, function () { return { file: file, converted: false, from: null }; });
  }

  var API = {
    version: 'upload-v1',
    prepareFile: prepareFile,
    CONVERT_LIMIT_MS: CONVERT_LIMIT_MS,
    CONVERTIBLE: CONVERTIBLE.slice(),
    config: config,
    limits: limits,          // UI copy MUST come from here, never a hard-coded string
    analyzeBytes: analyzeBytes,
    analyzeFile: analyzeFile,
    /* The one quality verdict. Every surface asks THIS — desktop, mobile, and
       anything downstream — so a threshold can never be restated. */
    qualityOf: qualityOf,
    qualityBucket: qualityBucket,
    /* The SOURCE-FILE question, kept separate from the DPI one (D-103). */
    sourceAdvisoryFor: sourceAdvisoryFor,
    SOURCE_ADVISE: SOURCE_ADVISE,
    // exposed for tests / tooling:
    _detectFormat: detectFormat,
    _parsePNG: parsePNG, _parseJPEG: parseJPEG, _parseSVG: parseSVG, _parsePDF: parsePDF
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = API;
  if (root) root.SLUpload = API;
})(typeof window !== 'undefined' ? window : null);
